import { cva, type VariantProps } from "class-variance-authority";
import { type ButtonHTMLAttributes, forwardRef } from "react";

import { cn } from "@krea/shared/utils";

import { Spinner } from "./Spinner";

const buttonSize = {
  default: "default",
  sm: "sm",
  lg: "lg",
  xl: "xl",
  icon: "icon",
} as const;

const buttonVariants = cva(
  [
    "inline-flex shrink-0 items-center justify-center gap-2 rounded-sm outline-hidden transition-all",
    "whitespace-nowrap font-semibold leading-[1.3]",
    "aria-invalid:border-red-500 focus-visible:border-primary focus-visible:ring-3 focus-visible:ring-primary/50 ",
    "cursor-pointer disabled:cursor-not-allowed disabled:opacity-50",
    "[&_svg:not([class*='size-'])]:size-4 has-svg:pointer-events-none has-svg:shrink-0", // icon styles
  ],
  {
    variants: {
      variant: {
        default: [
          "border border-primary bg-primary text-white hover:bg-primary/80",
        ],
        danger: [
          "border border-red-500 bg-red-500 text-white hover:bg-red-500/90 focus-visible:ring-red-500/20",
        ],
        success: [
          "bg-mint-500 text-white hover:bg-mint-500/90 focus-visible:ring-mint-500/20",
        ],
        secondary: [
          "border border-primary bg-white text-primary hover:bg-primary hover:text-white",
        ],
        ghost: ["hover:bg-primary-100 text-primary"],
        link: ["font-semibold text-primary underline-offset-4 hover:underline"],
      },
      size: {
        [buttonSize.default]: "h-10 px-4 py-2 text-base has-[>svg]:px-3",
        [buttonSize.sm]:
          "h-8 gap-1.5 rounded-md px-3 text-sm has-[>svg]:px-2.5",
        [buttonSize.lg]: "h-12 rounded-md px-6 text-lg has-[>svg]:px-4",
        [buttonSize.xl]: "h-16 rounded-md px-8 text-xl has-[>svg]:px-6",
        [buttonSize.icon]: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: buttonSize.default,
    },
    compoundVariants: [
      {
        size: buttonSize.icon,
        variant: "ghost",
        class: "size-6",
      },
    ],
  },
);

const spinnerVariants = cva("size-4 animate-spin text-white", {
  variants: {
    variant: {
      default: "text-white",
      secondary: "text-primary",
      danger: "text-red-100",
      success: "text-mint-100",
      ghost: "text-primary-100",
      link: "text-primary",
    },
    size: {
      [buttonSize.default]: "size-4",
      [buttonSize.sm]: "size-3",
      [buttonSize.lg]: "size-5",
      [buttonSize.xl]: "size-6",
      [buttonSize.icon]: "",
    },
  },
  defaultVariants: {
    variant: "default",
    size: buttonSize.default,
  },
});

type INativeButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

type IButtonVariantProps = VariantProps<typeof buttonVariants>;

interface IButtonWithLoadingProps
  extends INativeButtonProps,
    IButtonVariantProps {
  size: Exclude<
    (typeof buttonSize)[keyof typeof buttonSize],
    typeof buttonSize.icon
  >;
  loading?: true;
}

interface IButtonWithoutLoadingProps
  extends INativeButtonProps,
    IButtonVariantProps {
  loading?: false;
}

type IButtonProps = IButtonWithLoadingProps | IButtonWithoutLoadingProps;

const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      className,
      variant,

      size,
      children,
      loading = false,
      disabled,
      type = "button",
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={Boolean(disabled) || loading}
        {...props}
      >
        {loading && <Spinner className={spinnerVariants({ variant, size })} />}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants, type IButtonProps };
